// SOW item C — "Better Together" on the 3D model viewer.
//
// Sits beneath the part menu when a part is selected in the model. AMF's review: "most people will go
// to the 3D and not this page" — the viewer, not the part detail page, is where customers actually
// choose parts, so this is the surface that matters most.
//
// USES THE MACHINE-SCOPED ENDPOINT, not the cart one. The viewer always knows which machine is open,
// so the bill-of-materials gate can run at machine level — the strict version. Measured at Coventry,
// gating at site level instead would admit 9,077 parts that do not fit the machine on screen.
//
// Deliberately compact: it overlays a 3D model the user is working with, so it must not become a
// second page. Three suggestions, one line each, one tap to add.

import React, { useEffect, useState } from "react";
import { Box, Chip, CircularProgress, IconButton, Paper, Tooltip, Typography } from "@mui/material";
import { AddShoppingCart, Close } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { getPartCompanions } from "../../services/partRecommendationService";

const ViewerBetterTogether = ({
	partNumber,
	machineNumber,
	siteNumber,
	onAddToCart = null,
	showPrice = true,
}) => {
	const { t } = useTranslation();
	const [items, setItems] = useState([]);
	const [loading, setLoading] = useState(false);
	const [dismissed, setDismissed] = useState(false);

	useEffect(() => {
		let cancelled = false;
		// A new part selection revives a panel the user dismissed for the previous one — dismissing is
		// "not this one", not "never show me this".
		setDismissed(false);
		setItems([]);
		if (!partNumber || !machineNumber) return undefined;
		setLoading(true);
		getPartCompanions({ productNumber: partNumber, machineNumber, siteNumber, limit: 3 })
			.then((r) => {
				if (cancelled) return;
				setItems(r?.success && r.render ? (r.suggestions || []) : []);
			})
			.catch(() => { if (!cancelled) setItems([]); })
			.finally(() => { if (!cancelled) setLoading(false); });
		return () => { cancelled = true; };
	}, [partNumber, machineNumber, siteNumber]);

	if (dismissed || (!loading && !items.length)) return null;

	return (
		<Paper
			elevation={3}
			sx={{
				width: 300, mt: 1, p: 1.2,
				borderTop: "3px solid var(--primary-main)",
				pointerEvents: "auto",
			}}
		>
			<Box sx={{ display: "flex", alignItems: "center", mb: 0.6 }}>
				<Box sx={{ flex: 1 }}>
					<Typography sx={{ fontWeight: 700, fontSize: "12.5px" }}>
						{t("UI_TEXT.BETTER_TOGETHER", "Better Together")}
					</Typography>
					<Typography sx={{ fontSize: "10px", color: "text.secondary" }}>
						{t("UI_TEXT.BETTER_TOGETHER_SUB", "Commonly ordered with this part")}
					</Typography>
				</Box>
				<Tooltip title={t("BUTTON.HIDE", "Hide")}>
					{/* aria-label, not just a tooltip: a tooltip gives a sighted user the word on hover
					    and gives a screen reader nothing, so this was an unnamed button. */}
					<IconButton size="small" aria-label={t("BUTTON.HIDE", "Hide")}
						onClick={() => setDismissed(true)}>
						<Close sx={{ fontSize: 15 }} />
					</IconButton>
				</Tooltip>
			</Box>

			{loading && (
				<Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 1 }}>
					<CircularProgress size={14} />
					<Typography sx={{ fontSize: "11px", color: "text.secondary" }}>
						{t("UI_TEXT.CHECKING", "Checking…")}
					</Typography>
				</Box>
			)}

			{!loading && items.map((s) => (
				<Box key={s.product_number}
					sx={{ display: "flex", alignItems: "flex-start", gap: 0.8, py: 0.7,
						borderTop: "1px solid #f0f2f4" }}>
					<Box sx={{ flex: 1, minWidth: 0 }}>
						<Typography sx={{ fontSize: "11.5px", fontWeight: 600 }} noWrap
							title={s.description || s.product_number}>
							{s.description || s.product_number}
						</Typography>
						<Typography sx={{ fontSize: "10px", color: "text.secondary" }} noWrap>
							{s.product_number}
						</Typography>
						{/* The reason, always. A part number the customer cannot justify is not actionable —
						    this is the whole basis on which AMF agreed to trust the feature.
						    Grey, not red: in this UI red is the warning colour, and a suggestion is
						    information, not an alert. AMF: "suggestion should be gray, red looks warning". */}
						<Typography sx={{ fontSize: "9.5px", color: "text.secondary", mt: 0.2 }}>
							{s.reason}
						</Typography>
						{/* Price in the site's own currency, supplied by the API only when the caller's
						    role may see prices — null covers both "not permitted" and "not priced", and
						    neither should render as £0.00. */}
						{showPrice && s.price != null && (
							<Typography sx={{ fontSize: "11px", fontWeight: 700, mt: 0.2 }}>
								{new Intl.NumberFormat(undefined, {
									style: "currency", currency: s.currency || "USD",
								}).format(s.price)}
							</Typography>
						)}
					</Box>
					{s.rspl === "Critical" && (
						<Chip label={t("UI_TEXT.CRITICAL", "Critical")} size="small"
							sx={{ height: 16, fontSize: "8.5px", fontWeight: 700,
								backgroundColor: "rgba(237,31,39,0.10)", color: "var(--primary-main)" }} />
					)}
					{onAddToCart && (
						<Tooltip title={t("BUTTON.ADD_TO_CART", "Add to cart")}>
							{/* Named per part, not just "Add to cart": in a list of three identical
							    icon buttons an unqualified label cannot tell them apart. */}
							<IconButton size="small"
								aria-label={`${t("BUTTON.ADD_TO_CART", "Add to cart")}: ${s.description || s.product_number}`}
								onClick={() => onAddToCart(s)}>
								<AddShoppingCart sx={{ fontSize: 16, color: "var(--primary-main)" }} />
							</IconButton>
						</Tooltip>
					)}
				</Box>
			))}
		</Paper>
	);
};

export default ViewerBetterTogether;
